/** Общее для экранов охраны труда: диалог с причиной, выбор людей и работников, подписи состояний */
import {
  CREDENTIAL_KINDS,
  type CredentialState,
  OBSERVATION_STATUSES,
  type ObservationStatus,
  PERMIT_STATUSES,
  type PermitStatus,
  type SafetyWorker,
} from '@epc/contracts';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../lib/api';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, ErrorBox, type Tone } from '../../ui/feedback';
import { Field, Textarea } from '../../ui/form';

export const kindName = (k: keyof typeof CREDENTIAL_KINDS) => CREDENTIAL_KINDS[k];

export const permitTone = (s: PermitStatus, expired: boolean): Tone =>
  expired
    ? 'danger'
    : s === 'active'
      ? 'success'
      : s === 'suspended'
        ? 'danger'
        : s === 'done'
          ? 'warning'
          : s === 'cancelled'
            ? 'neutral'
            : s === 'closed'
              ? 'neutral'
              : 'info';

export function PermitBadge({ status, expired }: { status: PermitStatus; expired: boolean }) {
  return <Badge tone={permitTone(status, expired)}>{expired ? 'Срок вышел' : PERMIT_STATUSES[status]}</Badge>;
}

export const credentialTone: Record<CredentialState, Tone> = {
  valid: 'success',
  expiring: 'warning',
  expired: 'danger',
  revoked: 'neutral',
};
export const credentialLabel: Record<CredentialState, string> = {
  valid: 'Действует',
  expiring: 'Истекает',
  expired: 'Истёк',
  revoked: 'Аннулирован',
};

export function ObservationBadge({ status, overdue }: { status: ObservationStatus; overdue: boolean }) {
  if (overdue) return <Badge tone="danger">Меры просрочены</Badge>;
  return (
    <Badge tone={status === 'new' ? 'warning' : status === 'measures' ? 'info' : 'neutral'}>
      {OBSERVATION_STATUSES[status]}
    </Badge>
  );
}

/** Действие с обязательной причиной или заметкой */
export function ReasonDialog({
  title,
  label,
  confirm,
  required = true,
  busy,
  error,
  onCancel,
  onConfirm,
}: {
  title: string;
  label: string;
  confirm: string;
  required?: boolean;
  busy?: boolean;
  error?: unknown;
  onCancel: () => void;
  onConfirm: (text: string) => void;
}) {
  const [text, setText] = useState('');
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onCancel()}
      title={title}
      footer={
        <>
          <Button variant="ghost" className="h-11 md:h-10" onClick={onCancel}>
            Не надо
          </Button>
          <Button
            variant="primary"
            className="h-11 md:h-10"
            loading={busy}
            disabled={required && text.trim().length < 3}
            onClick={() => onConfirm(text.trim())}
          >
            {confirm}
          </Button>
        </>
      }
    >
      {error ? <ErrorBox error={error} className="mb-3" /> : null}
      <Field label={label}>
        <Textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} />
      </Field>
    </Dialog>
  );
}

/** Работники для выбора: с действующими видами допусков */
export function useSafetyWorkers(projectId: string | undefined) {
  return useQuery({
    queryKey: ['qa-safety-workers', projectId],
    queryFn: () => api<SafetyWorker[]>('/quality/safety/workers', { query: { projectId } }),
    enabled: Boolean(projectId),
  });
}

export function usePermitPeople(projectId: string | undefined) {
  return useQuery({
    queryKey: ['qa-permit-people', projectId],
    queryFn: () =>
      api<Array<{ id: string; fullName: string }>>('/quality/permits/people', { query: { projectId } }),
    enabled: Boolean(projectId),
    retry: false,
  });
}

/** ISO-момент из значения поля datetime-local */
export const localToIso = (v: string) => new Date(v).toISOString();
/** Значение для datetime-local из момента со сдвигом в часах от текущего */
export function localInput(hoursFromNow: number): string {
  const d = new Date(Date.now() + hoursFromNow * 3_600_000);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}
